import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTheme } from '../contexts/ThemeContext';
import { organizationService } from '../services/organization';
import {
  Organization, CreateOrganizationRequest,
  OrganizationRestaurant, CreateOrganizationRestaurantRequest,
  Employee, EmployeeRole,
} from '../types';
import { useForm } from 'react-hook-form';
import { ConfirmationModal } from '../components/ConfirmationModal';
import {
  Building2, UtensilsCrossed, Plus, Edit, Trash2, X,
  ChevronDown, ChevronRight, Phone, MapPin,
  ArrowLeft, PowerOff, Power,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { PageSkeleton, ButtonLoader } from '../components/LoadingStates';

// // ── Constants ────────────────────────────────────────────────────────────────
// const AVAILABLE_ROLES: { value: EmployeeRole; label: string }[] = [
//   { value: 'ADMIN', label: 'Admin' },
//   { value: 'MANAGER', label: 'Manager' },
//   { value: 'CASHIER', label: 'Cashier' },
//   { value: 'WAITER', label: 'Waiter' },
//   { value: 'CHEF', label: 'Chef' },
//   { value: 'KITCHEN_STAFF', label: 'Kitchen Staff' },
//   { value: 'RECEPTIONIST', label: 'Receptionist' },
// ];

const ROLE_COLORS: Record<string, string> = {
  ADMIN: 'bg-red-100 text-red-700',
  MANAGER: 'bg-purple-100 text-purple-700',
  CASHIER: 'bg-blue-100 text-blue-700',
  WAITER: 'bg-green-100 text-green-700',
  CHEF: 'bg-orange-100 text-orange-700',
  KITCHEN_STAFF: 'bg-yellow-100 text-yellow-700',
  RECEPTIONIST: 'bg-pink-100 text-pink-700',
};

// ── Form interfaces ───────────────────────────────────────────────────────────
interface OrgFormData { code: string; name: string; address?: string; mobile?: string; email?: string; }
interface RestaurantFormData { name: string; address?: string; mobile?: string; email?: string; }

export const OrganizationDetail: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const p = currentTheme.colors.primary;

  // ── Expanded state ────────────────────────────────────────────────────────
  const [expandedRestaurants, setExpandedRestaurants] = useState<Set<string>>(new Set());
  const toggleRest = (id: string) => setExpandedRestaurants(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // ── Edit org modal ────────────────────────────────────────────────────────
  const [editOrgOpen, setEditOrgOpen] = useState(false);
  const orgForm = useForm<OrgFormData>();

  // ── Restaurant modal ──────────────────────────────────────────────────────
  const [restModal, setRestModal] = useState<{ open: boolean; editing: OrganizationRestaurant | null }>({ open: false, editing: null });
  const restForm = useForm<RestaurantFormData>();

  // ── Confirmation modal ────────────────────────────────────────────────────
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean; title: string; message: string;
    onConfirm: () => void; type: 'warning' | 'danger' | 'info'; confirmText: string;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {}, type: 'warning', confirmText: 'Confirm' });

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: org, isLoading: orgLoading } = useQuery(
    ['org-detail', orgId],
    () => organizationService.getOrganizationById(orgId!),
    { enabled: !!orgId }
  );

  const { data: restaurants = [], isLoading: restsLoading } = useQuery(
    ['org-restaurants-detail', orgId],
    () => organizationService.getRestaurantsByOrganization(orgId!),
    { enabled: !!orgId }
  );

  const empQueries = useQuery(
    ['restaurant-employees-detail', Array.from(expandedRestaurants)],
    async () => {
      const res: Record<string, Employee[]> = {};
      for (const restId of Array.from(expandedRestaurants)) {
        try { res[restId] = await organizationService.getRestaurantEmployees(orgId!, restId); } catch { res[restId] = []; }
      }
      return res;
    },
    { enabled: expandedRestaurants.size > 0 && !!orgId, keepPreviousData: true }
  );
  const employeesByRestaurant: Record<string, Employee[]> = empQueries.data || {};

  // ── Mutations ─────────────────────────────────────────────────────────────
  const updateOrgM = useMutation(
    (d: Partial<CreateOrganizationRequest>) => organizationService.updateOrganization(orgId!, d),
    {
      onSuccess: () => { queryClient.invalidateQueries(['org-detail', orgId]); setEditOrgOpen(false); toast.success('Organization updated'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
    }
  );

  const toggleStatusM = useMutation(
    (active: boolean) => organizationService.toggleOrganizationStatus(orgId!, active),
    {
      onSuccess: (_, active) => {
        queryClient.invalidateQueries(['org-detail', orgId]);
        queryClient.invalidateQueries(['organizations-onboarding']);
        toast.success(active ? 'Organization activated' : 'Organization deactivated');
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
    }
  );

  const deleteOrgM = useMutation(
    () => organizationService.deleteOrganization(orgId!),
    {
      onSuccess: () => { navigate('/supperadmin'); toast.success('Organization deleted'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
    }
  );

  const createRestM = useMutation(
    (d: CreateOrganizationRestaurantRequest) => organizationService.createRestaurant(orgId!, d),
    {
      onSuccess: () => { queryClient.invalidateQueries(['org-restaurants-detail']); closeRestModal(); toast.success('Restaurant added'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
    }
  );
  const updateRestM = useMutation(
    ({ restId, d }: { restId: string; d: Partial<CreateOrganizationRestaurantRequest> }) =>
      organizationService.updateRestaurant(orgId!, restId, d),
    {
      onSuccess: () => { queryClient.invalidateQueries(['org-restaurants-detail']); closeRestModal(); toast.success('Restaurant updated'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
    }
  );
  const deleteRestM = useMutation(
    (restId: string) => organizationService.deleteRestaurant(orgId!, restId),
    {
      onSuccess: () => { queryClient.invalidateQueries(['org-restaurants-detail']); toast.success('Restaurant deleted'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
    }
  );

  // ── Helpers ───────────────────────────────────────────────────────────────
  const openEditOrg = () => {
    if (!org) return;
    orgForm.reset({ code: org.code, name: org.name, address: org.address, mobile: org.mobile, email: org.email });
    setEditOrgOpen(true);
  };

  const openRestModal = (rest?: OrganizationRestaurant) => {
    setRestModal({ open: true, editing: rest || null });
    restForm.reset(rest ? { name: rest.name, address: rest.address, mobile: rest.mobile, email: rest.email } : {});
  };
  const closeRestModal = () => { setRestModal({ open: false, editing: null }); restForm.reset(); };
  const onRestSubmit = (d: RestaurantFormData) => {
    if (restModal.editing) updateRestM.mutate({ restId: restModal.editing.id, d });
    else createRestM.mutate({ ...d, organizationId: orgId! });
  };

  if (orgLoading) return <PageSkeleton type="grid" />;
  if (!org) return (
    <div className="p-6">
      <button onClick={() => navigate('/supperadmin')} className="flex items-center gap-2 text-sm mb-4" style={{ color: p[600] }}>
        <ArrowLeft className="h-4 w-4" /> Back to Organizations
      </button>
      <p className="text-gray-500">Organization not found.</p>
    </div>
  );

  return (
    <div className="p-6">
      {/* Back */}
      <div className="flex justify-end mb-3">
        <button onClick={() => navigate('/supperadmin')}
          className="inline-flex items-center gap-1.5 text-sm hover:underline"
          style={{ color: p[600] }}>
          <ArrowLeft className="h-4 w-4" /> Back to Organizations
        </button>
      </div>

      {/* Org Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)]"
          style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: p[50] }}>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg" style={{ backgroundColor: p[100] }}>
              <Building2 className="h-6 w-6" style={{ color: p[600] }} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold" style={{ color: currentTheme.colors.text.primary }}>{org.name}</h1>
                {org.code && <span className="text-xs px-2 py-0.5 rounded font-mono" style={{ backgroundColor: p[100], color: p[700] }}>{org.code}</span>}
                <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', org.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600')}>
                  {org.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex flex-wrap gap-x-4 mt-0.5">
                {org.address && <span className="flex items-center gap-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}><MapPin className="h-3 w-3" />{org.address}</span>}
                {org.mobile && <span className="flex items-center gap-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}><Phone className="h-3 w-3" />{org.mobile}</span>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={openEditOrg}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: p[100], color: p[700] }}>
              <Edit className="h-4 w-4" /> Edit
            </button>
            <button
              onClick={() => toggleStatusM.mutate(!org.active)}
              disabled={toggleStatusM.isLoading}
              className={clsx('inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium', org.active ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-green-100 text-green-700 hover:bg-green-200')}>
              {org.active ? <><PowerOff className="h-4 w-4" /> Deactivate</> : <><Power className="h-4 w-4" /> Activate</>}
            </button>
            <button
              onClick={() => { if (window.confirm(`Delete "${org.name}"? This cannot be undone.`)) deleteOrgM.mutate(); }}
              disabled={deleteOrgM.isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </div>
        </div>
      </div>

      {/* ── Restaurants ─────────────────────────────────────────────────────── */}
      <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-base font-semibold" style={{ color: currentTheme.colors.text.primary }}>Restaurants</h2>
            <button onClick={() => openRestModal()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white"
              style={{ backgroundColor: p[600] }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = p[700])}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = p[600])}>
              <Plus className="h-4 w-4" /> Add Restaurant
            </button>
          </div>

          {restsLoading ? (
            <div className="py-8 text-center text-sm" style={{ color: currentTheme.colors.text.secondary }}>Loading...</div>
          ) : restaurants.length === 0 ? (
            <div className="py-10 text-center rounded-xl border border-dashed" style={{ borderColor: currentTheme.colors.border.primary }}>
              <UtensilsCrossed className="h-10 w-10 mx-auto mb-2" style={{ color: currentTheme.colors.text.secondary }} />
              <p className="text-sm mb-3" style={{ color: currentTheme.colors.text.secondary }}>No restaurants yet</p>
              <button onClick={() => openRestModal()} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-white rounded-lg btn-primary" style={{ backgroundColor: p[600] }}>
                <Plus className="h-4 w-4" /> Add Restaurant
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {restaurants.map(rest => {
                const isExpanded = expandedRestaurants.has(rest.id);
                const employees: Employee[] = employeesByRestaurant[rest.id] || [];
                return (
                  <div key={rest.id} className="rounded-xl border overflow-hidden"
                    style={{ borderColor: currentTheme.colors.border.primary }}>
                    {/* Restaurant row */}
                    <div className="flex items-center justify-between px-4 py-3 cursor-pointer"
                      style={{ backgroundColor: isExpanded ? '#f9fafb' : '#fff' }}
                      onClick={() => toggleRest(rest.id)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="p-0.5 rounded flex-shrink-0" style={{ color: p[500] }}>
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </span>
                        <div className="p-1.5 rounded-md flex-shrink-0" style={{ backgroundColor: '#f0fdf4' }}>
                          <UtensilsCrossed className="h-4 w-4 text-green-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: currentTheme.colors.text.primary }}>{rest.name}</p>
                          <div className="flex flex-wrap gap-x-3">
                            {rest.address && <span className="flex items-center gap-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}><MapPin className="h-3 w-3" />{rest.address}</span>}
                            {rest.mobile && <span className="flex items-center gap-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}><Phone className="h-3 w-3" />{rest.mobile}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', rest.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600')}>{rest.active ? 'Active' : 'Inactive'}</span>
                        {isExpanded && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{employees.length} emp</span>}
                        <button onClick={e => { e.stopPropagation(); openRestModal(rest); }} className="p-1.5 rounded-lg" style={{ color: p[600], backgroundColor: p[50] }} title="Edit">
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={e => { e.stopPropagation(); setConfirmModal({
                          isOpen: true, title: 'Delete Restaurant', type: 'danger', confirmText: 'Delete',
                          message: `Delete "${rest.name}"?`,
                          onConfirm: () => { deleteRestM.mutate(rest.id); setConfirmModal(pp => ({ ...pp, isOpen: false })); },
                        }); }} className="p-1.5 rounded-lg bg-red-50 text-red-600" title="Delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Employees */}
                    {isExpanded && (
                      <div className="px-4 pb-3 pt-1 bg-white border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                        {employees.length === 0 ? (
                          <div className="flex items-center gap-2 py-2 text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                            No employees assigned. Add or assign employees from the Employee Management page.
                          </div>
                        ) : (
                          <div className="space-y-1.5 mt-1">
                            {employees.map(emp => {
                              const empRoles = (emp.restaurantRoles.find(rr => rr.restaurantId === rest.id)?.roles || []) as EmployeeRole[];
                              return (
                                <div key={emp.id} className="flex items-center justify-between px-3 py-2 rounded-lg border"
                                  style={{ borderColor: currentTheme.colors.border.primary }}>
                                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                                      style={{ backgroundColor: p[100], color: p[700] }}>
                                      {(emp.firstName || emp.name || '?').charAt(0).toUpperCase()}
                                    </div>
                                    <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                                      {[emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(' ') || emp.name || '—'}
                                    </span>
                                    <div className="flex gap-1 flex-wrap">
                                      {empRoles.map(role => (
                                        <span key={role} className={clsx('px-1.5 py-0.5 rounded-full text-xs font-medium', ROLE_COLORS[role] || 'bg-gray-100 text-gray-700')}>
                                          {role.replace(/_/g, ' ')}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      {/* ── Edit Organization Modal ───────────────────────────────────────────── */}
      {editOrgOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Building2 className="h-5 w-5" style={{ color: p[600] }} /> Edit Organization
              </h2>
              <button onClick={() => setEditOrgOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
            </div>
            <form onSubmit={orgForm.handleSubmit(d => updateOrgM.mutate(d))} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                  <input {...orgForm.register('code', { required: 'Required' })} className="ui-input" />
                  {orgForm.formState.errors.code && <p className="mt-1 text-xs text-red-600">{orgForm.formState.errors.code.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input {...orgForm.register('name', { required: 'Required' })} className="ui-input" />
                  {orgForm.formState.errors.name && <p className="mt-1 text-xs text-red-600">{orgForm.formState.errors.name.message}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input {...orgForm.register('address')} className="ui-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input {...orgForm.register('mobile')} className="ui-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input {...orgForm.register('email')} type="email" className="ui-input" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditOrgOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" disabled={updateOrgM.isLoading} className="px-4 py-2 text-white rounded-lg btn-primary disabled:opacity-50 flex items-center" style={{ backgroundColor: p[600] }}>
                  {updateOrgM.isLoading ? <ButtonLoader text="Saving..." /> : 'Update Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Restaurant Modal ──────────────────────────────────────────────────── */}
      {restModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <UtensilsCrossed className="h-5 w-5 text-green-600" />
                {restModal.editing ? 'Edit Restaurant' : 'Add Restaurant'}
              </h2>
              <button onClick={closeRestModal} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
            </div>
            <form onSubmit={restForm.handleSubmit(onRestSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant Name *</label>
                <input {...restForm.register('name', { required: 'Required' })} placeholder="e.g., Main Dining"
                  className="ui-input" />
                {restForm.formState.errors.name && <p className="mt-1 text-xs text-red-600">{restForm.formState.errors.name.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input {...restForm.register('address')} className="ui-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input {...restForm.register('mobile')} className="ui-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input {...restForm.register('email')} type="email" className="ui-input" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeRestModal} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" disabled={createRestM.isLoading || updateRestM.isLoading}
                  className="px-4 py-2 text-white rounded-lg btn-primary disabled:opacity-50 flex items-center" style={{ backgroundColor: p[600] }}>
                  {(createRestM.isLoading || updateRestM.isLoading) ? <ButtonLoader text="Saving..." /> : restModal.editing ? 'Update Restaurant' : 'Add Restaurant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        type={confirmModal.type}
        isLoading={deleteRestM.isLoading}
      />
    </div>
  );
};
